import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';

const InputWrapper = styled.div`
    position: relative;
    display: grid;
    width: 100%;
    max-width: 100%;
`;

const StyledInput = styled.textarea<{ $textAlign: 'left' | 'center' | 'right', $colorMode: 'light' | 'dark', $padding: string }>`
    grid-area: 1 / 1;
    width: auto;
    min-width: 120px;
    max-width: 100%;
    height: 1.2em;
    border: none;
    outline: none;
    color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.black};
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: inherit;
    text-align: ${(props) => props.$textAlign};
    line-height: 1.2em;
    padding: ${props => props.$padding};
    white-space: pre-wrap;
    text-wrap: stable;
    resize: none;
    overflow: hidden;
    box-sizing: border-box;
    cursor: text;
    background-color: ${Colors.transparent};
    border-bottom: 1px solid ${Colors.transparent};
    transition: border 0.2s ease-in-out;

    &:hover {
        border-bottom: 1px solid ${Colors.transparent};
    }

    &:focus {
        border-bottom: 1px solid ${Colors.transparent};
    }

    &:disabled {
        border-bottom: 1px solid ${Colors.transparent};
        background-color: ${Colors.transparent};
        color: ${Colors.grey6};
        cursor: pointer;
    }
`;

interface InputSimpleProps {
    value?: string | number;
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    disabled?: boolean;
    textAlign?: 'left' | 'center' | 'right'; 
    colorMode?: 'light' | 'dark';
    onEnterPress?: () => void;    
    padding?: string; 
    initialRows?: number;
}

const InputSimple = React.forwardRef<HTMLTextAreaElement, InputSimpleProps>(({ 
    value: initialValue = '', 
    onChange, 
    placeholder, 
    disabled = false,
    textAlign = 'center',
    colorMode = 'light',
    onEnterPress,
    padding = '6px 12px',
    initialRows = 1
}, ref) => {
    const [value, setValue] = useState(initialValue.toString());
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const rafRef = useRef<number>();

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(e.target.value);
        onChange && onChange(e);
        
        // Cancel any pending RAF
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }
        
        // Schedule immediate height adjustment
        rafRef.current = requestAnimationFrame(adjustHeight);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Check if Enter key is pressed (without Shift) and onEnterPress prop exists
        if (e.key === 'Enter' && !e.shiftKey && onEnterPress) {
            e.preventDefault(); // Prevent newline
            onEnterPress();
            inputRef.current?.blur();
        }
    };

    const adjustHeight = useCallback(() => {
        if (inputRef.current) {
            // Preserve scroll position
            const scrollPos = window.scrollY;
            
            // Set height to a single line height to measure
            // Reset to '0' first to force re-measurement if content has shrunk.
            inputRef.current.style.height = '0px';
            inputRef.current.style.height = '1.2em';

            // Calculate the height of one line (1.2em + padding).
            // This clientHeight is the exact minimum height for one line of content.
            const oneLineHeight = inputRef.current.clientHeight;

            // Now, reset to '0' again to get an accurate scrollHeight without initial height interfering.
            inputRef.current.style.height = '0px';
            
            // Calculate the total height needed for the content (scrollHeight)
            const contentHeight = inputRef.current.scrollHeight;
            
            // Calculate the minimum required height based on initialRows
            // We use oneLineHeight * initialRows
            const minHeight = oneLineHeight * initialRows;

            // The actual height should be the greater of the minimum rows required or the height needed for content
            const height = Math.max(minHeight, contentHeight);
            
            inputRef.current.style.height = `${height}px`;
            
            // Restore scroll position
            window.scrollTo(0, scrollPos);
        }
    }, [initialRows]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = requestAnimationFrame(adjustHeight);
        });
        
        if (inputRef.current) {
            resizeObserver.observe(inputRef.current);
        }

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            resizeObserver.disconnect();
        };
    }, [adjustHeight]);

    // Sync with external value changes and adjust height
    useEffect(() => {
        setValue(initialValue.toString());
        // Ensure height adjustment happens after state update
        Promise.resolve().then(() => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = requestAnimationFrame(() => {
                adjustHeight();
                // Double check height after a brief delay to catch any late updates
                setTimeout(adjustHeight, 50);
            });
        });
    }, [initialValue, adjustHeight]);

    return (
        <InputWrapper>
            <StyledInput
                ref={(element) => {
                    // Assign to both refs
                    inputRef.current = element;
                    if (typeof ref === 'function') {
                        ref(element);
                    } else if (ref) {
                        ref.current = element;
                    }
                }}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                $textAlign={textAlign}
                $colorMode={colorMode}
                $padding={padding}
            />
        </InputWrapper>
    );
});

export default InputSimple;